<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar class="custom-toolbar">
        <ion-buttons slot="start">
          <ion-menu-button color="light"></ion-menu-button>
        </ion-buttons>
        <ion-title class="custom-title">Apply for Leave</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="fetchLeaveBalances" color="light" class="refresh-btn">
            <ion-icon slot="icon-only" :icon="refreshOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true" class="leave-content">
      <div class="leave-container">
        
        <!-- Live Available Balances Section -->
        <div class="balances-section">
          <div class="section-header">
            <ion-icon :icon="briefcaseOutline" class="section-icon"></ion-icon>
            <h3>Available Leave Balances ({{ fiscalYearTitle || currentYear }})</h3>
          </div>

          <div v-if="loadingBalances" class="balances-loading">
            <ion-spinner name="crescent" color="primary"></ion-spinner>
            <span>Fetching leave balances...</span>
          </div>

          <div v-else-if="balances.length > 0" class="leave-balance-grid">
            <div 
              v-for="b in balances" 
              :key="b.id" 
              class="balance-card"
              :class="getCardTheme(b.leave_type ? b.leave_type.title : '')"
            >
              <div class="card-glow"></div>
              <div class="card-header-type">
                <ion-icon :icon="getLeaveIcon(b.leave_type ? b.leave_type.title : '')" class="type-icon"></ion-icon>
                <span class="type-title">{{ b.leave_type ? b.leave_type.title : 'N/A' }}</span>
              </div>
              <div class="card-balance-number">{{ b.balance_days }}</div>
              <div class="card-balance-label">Days Available</div>
              <div class="card-footer-stats">
                <span>Used: {{ b.used_days }}</span>
                <span>Allowed: {{ b.total_days }}</span>
              </div>
            </div>
          </div>

          <div v-else class="balances-error">
            <ion-icon :icon="alertCircleOutline" class="error-icon"></ion-icon>
            <p>No leave balances allocated for you this year. Please contact HR.</p>
          </div>
        </div>

        <!-- Leave Requisition Form Card -->
        <div class="action-panel-card">
          <div class="panel-header">
            <ion-icon :icon="createOutline" class="panel-header-icon"></ion-icon>
            <h3>Leave Requisition Form</h3>
          </div>
          
          <form @submit.prevent="submitApplication" class="leave-form">
            <!-- Leave Type Select -->
            <div class="form-group-custom">
              <ion-item class="form-item-custom" lines="none">
                <ion-icon :icon="optionsOutline" slot="start" class="field-icon"></ion-icon>
                <ion-label position="floating" class="custom-label">Leave Type <span class="required">*</span></ion-label>
                <ion-select v-model="form.leave_type_id" required interface="action-sheet" class="custom-select">
                  <ion-select-option v-for="b in balances" :key="b.id" :value="b.leave_type_id">
                    {{ b.leave_type ? b.leave_type.title : 'N/A' }} (Available: {{ b.balance_days }} days)
                  </ion-select-option>
                </ion-select>
              </ion-item>
            </div>

            <!-- Start Date Input -->
            <div class="form-group-custom" style="padding-top: 10px;">
              <ion-item class="form-item-custom" lines="none" style="display:flex; justify-content:space-between; align-items:center;">
                <ion-icon :icon="calendarOutline" slot="start" class="field-icon"></ion-icon>
                <ion-label class="custom-label">Start Date <span class="required">*</span></ion-label>
                <ion-datetime
                  display-format="YYYY-MM-DD"
                  placeholder="Select Date"
                  v-model="form.start_date"
                  mode="ios"
                  :min="startDateMin"
                  :max="startDateMax"
                ></ion-datetime>
              </ion-item>
            </div>

            <!-- End Date Input -->
            <div class="form-group-custom" style="padding-top: 10px;">
              <ion-item class="form-item-custom" lines="none" style="display:flex; justify-content:space-between; align-items:center;">
                <ion-icon :icon="calendarOutline" slot="start" class="field-icon"></ion-icon>
                <ion-label class="custom-label">End Date <span class="required">*</span></ion-label>
                <ion-datetime
                  display-format="YYYY-MM-DD"
                  placeholder="Select Date"
                  v-model="form.end_date"
                  mode="ios"
                  :min="endDateMin"
                  :max="endDateMax"
                ></ion-datetime>
              </ion-item>
            </div>

            <!-- Duration Summary Box -->
            <transition name="fade-slide">
              <div v-if="calculatedDays > 0" class="duration-summary-box">
                <ion-icon :icon="hourglassOutline" class="duration-icon"></ion-icon>
                <span class="duration-text">Leave Duration: <strong>{{ calculatedDays }} day(s)</strong></span>
              </div>
            </transition>

            <!-- Reason Input -->
            <div class="form-group-custom">
              <ion-item class="form-item-custom" lines="none">
                <ion-icon :icon="chatboxEllipsesOutline" slot="start" class="field-icon"></ion-icon>
                <ion-label position="floating" class="custom-label">Reason <span class="required">*</span></ion-label>
                <ion-input v-model="form.reason" required placeholder="State your reason for leave" class="custom-input"></ion-input>
              </ion-item>
            </div>

            <!-- Attachment Document (PDF/Images) -->
            <div class="form-group-custom">
              <ion-item class="form-item-custom" lines="none">
                <ion-icon :icon="documentAttachOutline" slot="start" class="field-icon"></ion-icon>
                <ion-label position="stacked" class="custom-label">Attachment Document (PDF/Images) <span v-if="isSickLeave" class="required">*</span></ion-label>
                <input type="file" ref="fileInput" @change="onFileSelected" :required="isSickLeave" accept=".pdf,image/*" class="custom-file-input">
              </ion-item>
            </div>

            <div class="btn-container">
              <button type="submit" class="btn-submit">
                <ion-icon :icon="paperPlaneOutline" class="btn-submit-icon"></ion-icon>
                <span>Submit Requisition</span>
              </button>
            </div>
          </form>
        </div>

      </div>
    </ion-content>
  </ion-page>
</template>

<script>
/* eslint-disable */
import { 
  IonButtons, 
  IonContent, 
  IonHeader, 
  IonMenuButton, 
  IonPage, 
  IonTitle, 
  IonToolbar, 
  IonItem, 
  IonLabel, 
  IonSelect, 
  IonSelectOption, 
  IonInput, 
  IonButton, 
  IonIcon,
  IonSpinner,
  IonDatetime,
  alertController 
} from '@ionic/vue';
import { 
  refreshOutline, 
  briefcaseOutline, 
  alertCircleOutline, 
  createOutline, 
  optionsOutline, 
  calendarOutline, 
  hourglassOutline, 
  chatboxEllipsesOutline, 
  paperPlaneOutline, 
  medicalOutline, 
  happyOutline, 
  airplaneOutline, 
  documentTextOutline,
  documentAttachOutline 
} from 'ionicons/icons';

export default {
  name: 'LeaveApply',
  components: {
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar,
    IonItem,
    IonLabel,
    IonSelect,
    IonSelectOption,
    IonInput,
    IonButton,
    IonIcon,
    IonSpinner,
    IonDatetime
  },
  data() {
    return {
      // Icons
      refreshOutline,
      briefcaseOutline,
      alertCircleOutline,
      createOutline,
      optionsOutline,
      calendarOutline,
      hourglassOutline,
      chatboxEllipsesOutline,
      paperPlaneOutline,
      documentAttachOutline,

      balances: [],
      loadingBalances: true,
      fiscalYearTitle: '',
      currentYear: new Date().getFullYear(),
      todayDateStr: new Date().toISOString().split('T')[0],
      calculatedDays: 0,
      selectedFile: null,
      form: {
        leave_type_id: '',
        start_date: '',
        end_date: '',
        reason: '',
      }
    }
  },
  created() {
    this.fetchLeaveBalances();
  },
  watch: {
    'form.leave_type_id'() {
      this.form.start_date = '';
      this.form.end_date = '';
      this.calculatedDays = 0;
      this.selectedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    'form.start_date'(newVal) {
      this.calculateDays();
      if (newVal) {
        let cleanDate = newVal;
        if (cleanDate.includes('T')) {
          cleanDate = cleanDate.split('T')[0];
        }
        this.fetchLeaveBalances(cleanDate);
      } else {
        this.fetchLeaveBalances();
      }
    },
    'form.end_date'() {
      this.calculateDays();
    }
  },
  computed: {
    isSickLeave() {
      const selected = this.balances.find(b => b.leave_type_id === this.form.leave_type_id);
      if (selected && selected.leave_type) {
        const title = selected.leave_type.title.toLowerCase();
        const code = selected.leave_type.short_code.toLowerCase();
        return title.includes('sick') || code.includes('sick');
      }
      return false;
    },
    thirtyDaysAgoStr() {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      return d.toISOString().split('T')[0];
    },
    startDateMin() {
      // All leave types may be back-dated up to 30 days, so employees can cover
      // past absent days retroactively (backend rejects dates with attendance).
      return this.thirtyDaysAgoStr;
    },
    startDateMax() {
      return this.isSickLeave ? this.todayDateStr : undefined;
    },
    endDateMin() {
      return this.form.start_date || this.thirtyDaysAgoStr;
    },
    endDateMax() {
      return this.isSickLeave ? this.todayDateStr : undefined;
    }
  },
  methods: {
    async presentAlert(title, message) {
      const alert = await alertController.create({
        header: title,
        message: message,
        buttons: ['OK'],
      });
      await alert.present();
    },
    fetchLeaveBalances(targetDate = null) {
      this.loadingBalances = true;
      const data = {
        username: window.localStorage.username,
        password: window.localStorage.password
      };
      if (targetDate) {
        data.date = targetDate;
      }

      // Pass skipLoading: true to bypass the global fullscreen spinner overlay
      this.$http.post('hr/getLeaveBalances', data, { skipLoading: true })
        .then(response => {
          if (response.data.status) {
            this.balances = response.data.balances;
            if (response.data.fiscal_year) {
              this.fiscalYearTitle = response.data.fiscal_year.title;
            } else {
              this.fiscalYearTitle = '';
            }
          }
          this.loadingBalances = false;
        })
        .catch(err => {
          console.error(err);
          this.loadingBalances = false;
        });
    },
    calculateDays() {
      if (this.form.start_date && this.form.end_date) {
        let startStr = this.form.start_date;
        if (startStr.includes('T')) startStr = startStr.split('T')[0];
        
        let endStr = this.form.end_date;
        if (endStr.includes('T')) endStr = endStr.split('T')[0];

        const start = new Date(startStr);
        const end = new Date(endStr);
        if (end >= start) {
          const diffTime = Math.abs(end - start);
          this.calculatedDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
        } else {
          this.calculatedDays = 0;
        }
      } else {
        this.calculatedDays = 0;
      }
    },
    getLeaveIcon(title) {
      const t = title.toLowerCase();
      if (t.includes('sick')) return medicalOutline;
      if (t.includes('casual')) return happyOutline;
      if (t.includes('annual') || t.includes('earn')) return airplaneOutline;
      return documentTextOutline;
    },
    getCardTheme(title) {
      const t = title.toLowerCase();
      if (t.includes('sick')) return 'sick-theme';
      if (t.includes('casual')) return 'casual-theme';
      if (t.includes('annual') || t.includes('earn')) return 'annual-theme';
      return 'default-theme';
    },
    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 10 * 1024 * 1024) {
          this.presentAlert('File Too Large', 'Maximum file size allowed is 10MB.');
          event.target.value = '';
          this.selectedFile = null;
          return;
        }
        this.selectedFile = file;
      } else {
        this.selectedFile = null;
      }
    },
    async submitApplication() {
      if (this.calculatedDays <= 0) {
        this.presentAlert('Validation Error', 'Invalid leave duration.');
        return;
      }

      // Check balance limit
      const selectedBalance = this.balances.find(b => b.leave_type_id === this.form.leave_type_id);
      if (!selectedBalance || selectedBalance.balance_days < this.calculatedDays) {
        this.presentAlert('Insufficient Balance', `You only have ${selectedBalance ? selectedBalance.balance_days : 0} days remaining for this leave type.`);
        return;
      }
      
      let finalStartDate = this.form.start_date;
      if (finalStartDate && finalStartDate.includes('T')) {
        finalStartDate = finalStartDate.split('T')[0];
      }
      
      let finalEndDate = this.form.end_date;
      if (finalEndDate && finalEndDate.includes('T')) {
        finalEndDate = finalEndDate.split('T')[0];
      }

      const formData = new FormData();
      formData.append('username', window.localStorage.username);
      formData.append('password', window.localStorage.password);
      formData.append('leave_type_id', this.form.leave_type_id);
      formData.append('start_date', finalStartDate);
      formData.append('end_date', finalEndDate);
      formData.append('reason', this.form.reason);
      if (this.selectedFile) {
        formData.append('attachment', this.selectedFile);
      }

      // Fullscreen loader handled automatically by global interceptor since we don't pass skipLoading
      this.$http.post('hr/applyLeave', formData)
        .then(response => {
          if (response.data.status) {
            this.presentAlert('Success', response.data.message);
            // Reset form
            this.form.leave_type_id = '';
            this.form.start_date = '';
            this.form.end_date = '';
            this.form.reason = '';
            this.selectedFile = null;
            if (this.$refs.fileInput) {
              this.$refs.fileInput.value = '';
            }
            this.calculatedDays = 0;
            this.fetchLeaveBalances();
          } else {
            this.presentAlert('Failed', response.data.message);
          }
        })
        .catch(err => {
          // Managed globally by interceptors
        });
    }
  }
}
</script>

<style scoped>
/* Core Styles */
.leave-content {
  --background: #f4f6fc;
}

.leave-container {
  padding: 16px;
  max-width: 600px;
  margin: 0 auto;
}

/* Custom Toolbar Styles */
.custom-toolbar {
  --background: linear-gradient(135deg, #102a43 0%, #243b53 100%);
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.custom-title {
  font-weight: 700;
  font-size: 1.15rem;
  letter-spacing: 0.5px;
}

.refresh-btn {
  --color: #fff;
}

/* Section Header Styles */
.balances-section {
  margin-bottom: 24px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.section-icon {
  font-size: 1.4rem;
  color: #486581;
}

.section-header h3 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #102a43;
}

/* Balances Loading state */
.balances-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  gap: 12px;
  color: #627d98;
}

/* Bento Grid Balances */
.leave-balance-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 12px;
}

.balance-card {
  position: relative;
  border-radius: 16px;
  padding: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.02);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.balance-card:active {
  transform: scale(0.98);
}

.card-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
}

/* Theme Colors for Glow Line */
.sick-theme .card-glow {
  background: linear-gradient(90deg, #ff8a00, #e52e71);
}
.sick-theme .type-icon {
  color: #e52e71;
}

.casual-theme .card-glow {
  background: linear-gradient(90deg, #11998e, #38ef7d);
}
.casual-theme .type-icon {
  color: #11998e;
}

.annual-theme .card-glow {
  background: linear-gradient(90deg, #00c6ff, #0072ff);
}
.annual-theme .type-icon {
  color: #0072ff;
}

.default-theme .card-glow {
  background: linear-gradient(90deg, #70a1ff, #1e90ff);
}
.default-theme .type-icon {
  color: #1e90ff;
}

/* Card Elements */
.card-header-type {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.type-icon {
  font-size: 1.25rem;
}

.type-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: #334e68;
}

.card-balance-number {
  font-size: 2.2rem;
  font-weight: 800;
  color: #102a43;
  line-height: 1.1;
  margin-bottom: 2px;
}

.card-balance-label {
  font-size: 0.65rem;
  font-weight: 600;
  color: #829ab1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}

.card-footer-stats {
  display: flex;
  justify-content: space-between;
  font-size: 0.68rem;
  font-weight: 600;
  color: #486581;
  border-top: 1px dashed #e4e7eb;
  padding-top: 8px;
}

/* Form Panel Card */
.action-panel-card {
  background: #fff;
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.01);
  margin-bottom: 24px;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  border-bottom: 1px solid #f0f4f8;
  padding-bottom: 12px;
}

.panel-header-icon {
  font-size: 1.35rem;
  color: #0072ff;
}

.panel-header h3 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #102a43;
}

/* Custom Input Fields */
.form-group-custom {
  margin-bottom: 16px;
}

.form-item-custom {
  --background: #f8fafc;
  --border-radius: 12px;
  --padding-start: 12px;
  --inner-padding-end: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-item-custom:focus-within {
  border-color: #0072ff;
  box-shadow: 0 0 0 3px rgba(0, 114, 255, 0.1);
}

.field-icon {
  color: #94a3b8;
  margin-right: 8px;
  font-size: 1.25rem;
}

.custom-label {
  color: #64748b;
  font-weight: 600;
  font-size: 0.85rem !important;
}

.custom-input {
  --color: #0f172a;
  font-weight: 600;
  font-size: 0.95rem;
}

.custom-select {
  width: 100%;
  font-weight: 600;
  font-size: 0.95rem;
  color: #0f172a;
  --placeholder-color: #94a3b8;
}

.required {
  color: #ef4444;
}

/* Duration Summary Widget */
.duration-summary-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  padding: 12px 16px;
  border-radius: 12px;
  margin-bottom: 16px;
}

.duration-icon {
  font-size: 1.35rem;
  color: #2563eb;
}

.duration-text {
  font-size: 0.9rem;
  color: #1e3a8a;
  font-weight: 500;
}

.duration-text strong {
  font-weight: 700;
  color: #2563eb;
}

/* Submit Button */
.btn-container {
  margin-top: 24px;
}

.btn-submit {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(135deg, #0072ff 0%, #00c6ff 100%);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 14px;
  font-size: 0.95rem;
  font-weight: 700;
  box-shadow: 0 4px 15px rgba(0, 114, 255, 0.25);
  transition: transform 0.15s ease, opacity 0.15s ease;
}

.btn-submit:active {
  transform: scale(0.98);
}

.btn-submit-icon {
  font-size: 1.25rem;
}

/* Error fallback */
.balances-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #fff8f8;
  border: 1px solid #ffe3e3;
  border-radius: 16px;
  color: #c92a2a;
  text-align: center;
}

.error-icon {
  font-size: 2rem;
  margin-bottom: 8px;
}

.balances-error p {
  margin: 0;
  font-weight: 600;
  font-size: 0.9rem;
}

/* Transition Animations */
.fade-slide-enter-active, .fade-slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.custom-file-input {
  width: 100%;
  padding: 8px 0;
  font-size: 0.9rem;
  color: #475569;
  font-weight: 500;
  border: none;
  background: transparent;
  outline: none;
}
.custom-file-input::-webkit-file-upload-button {
  background: #0072ff;
  border: none;
  color: #fff;
  padding: 6px 12px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  margin-right: 8px;
  font-size: 0.8rem;
  transition: opacity 0.2s;
}
.custom-file-input::-webkit-file-upload-button:hover {
  opacity: 0.9;
}
</style>
